import React, { Fragment, memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type {
  AiDocumentReviewConfig,
  AiDocumentReviewConfigFormValues,
} from 'src/interfaces/aiDocumentReviewConfig'
import { buildFormValues } from 'src/utils/aiReviewConfigPayload'
import { AiReviewConfigFields } from './AiReviewConfigFields'
import { AiReviewConfigSubmitButton } from './AiReviewConfigSubmitButton'
import { aiReviewConfigValidator } from './AiReviewConfigForm.validator'

const FORM_ID = 'ai-review-config-form'

interface Props {
  isOpen: boolean
  /** `null` creates; a config edits it. */
  config: AiDocumentReviewConfig | null
  onClose(): void
  onSubmit(values: AiDocumentReviewConfigFormValues): void
}

const AiReviewConfigFormModalComponent = ({ isOpen, config, onClose, onSubmit }: Props) => {
  const initialValues = useMemo(() => {
    return buildFormValues(config)
  }, [config])

  const isEdit = config !== null

  if (!isOpen) {
    // Unmounting resets Formik, so the next open starts from the right row's values.
    return null
  }

  return (
    <Formik<AiDocumentReviewConfigFormValues>
      initialValues={initialValues}
      validationSchema={aiReviewConfigValidator}
      onSubmit={onSubmit}
    >
      <CommonModal
        isOpen={isOpen}
        title={isEdit ? 'Edit AI review configuration' : 'New AI review configuration'}
        onClose={onClose}
        className={tw`max-w-[560px]`}
        buttons={
          <Fragment>
            <Button size="small" color="grey" onClick={onClose}>
              Cancel
            </Button>
            <AiReviewConfigSubmitButton formId={FORM_ID} isEdit={isEdit} />
          </Fragment>
        }
      >
        <Form id={FORM_ID} data-testid="ai-review-config-form">
          <AiReviewConfigFields isEdit={isEdit} config={config} />
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const AiReviewConfigFormModal = memo(AiReviewConfigFormModalComponent)
